/**
 * SponsorshipOfferDialog
 *
 * Review a company's sponsorship offer — the twin of the invitation review
 * (3 Oct 2026): the company's mark and its question in the header, what
 * accepting does, and the same answers an invitation has. Opened from:
 *   1. the offer card (Home) and the bell's Invitations tab — "Review offer";
 *   2. the activation email's deep-link (`/sponsorship/offer?token=…`) — the
 *      landing page redirects to `/dashboard?sponsorshipToken=…`.
 *
 * Flow:
 *   Accept  → mutates `useAcceptSponsorshipOffer` → journey funding
 *             flips to `sponsored_b2b`. Dashboard re-renders.
 *
 * A journey the talent already paid for (`offer.takeover`, 3 Oct 2026): the
 * company takes it over. The dialog says so instead of promising an unlock —
 * with Pro, that Pro keeps covering their other journeys; with a paid unlock,
 * the refund the server computed for its unused part.
 *   Decline → opens the DeclineConfirm AlertDialog. On confirm,
 *             mutates `useDeclineSponsorshipOffer` → company is
 *             auto-refunded, journey stays `invited_only`.
 *   Not now → the offer's own defer (`useDeferSponsorshipOffer`), exactly an
 *             invitation's "Not now": it leaves Home and the bell's count for
 *             the defer window and stays listed in the Invitations tab. Shown
 *             only while not already put off — then the dialog says until when.
 *             Offers never expire.
 */

import { apiErrorCode, DOMAIN_ERROR_CODES } from '@tonnie/foundation';
import { useState } from 'react';
import { useCountryName, useLocale, useTranslation } from '@tonnie/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tonnie/ui';
import { Button } from '@tonnie/ui';
import {
  useAcceptSponsorshipOffer,
  useDeferSponsorshipOffer,
  useSponsorshipOffer,
} from '@tonnie/sdk';
import { CheckCircle2, Heart, Loader2, Plane, Sparkles, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { CompanyMark } from '@/features/platform/invitations/components/CompanyMark';
import { JourneyNameLine } from '@/features/platform/journey/components/JourneyNameLine';
import { SponsorshipDeclineConfirm } from './SponsorshipDeclineConfirm';

export interface SponsorshipOfferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sponsorship row id. The token in the email IS the sponsorship id. */
  sponsorshipId: string | null;
  /** Optional callback fired after accept (for cache invalidation / toast). */
  onAccepted?: () => void;
  /** Optional callback fired after decline. */
  onDeclined?: () => void;
}

export function SponsorshipOfferDialog({
  open,
  onOpenChange,
  sponsorshipId,
  onAccepted,
  onDeclined,
}: SponsorshipOfferDialogProps) {
  const { t } = useTranslation();
  const countryName = useCountryName();
  const { formatCurrency, formatDate } = useLocale();
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const offer = useSponsorshipOffer(open ? sponsorshipId : null);
  const accept = useAcceptSponsorshipOffer();
  const defer = useDeferSponsorshipOffer();

  const company = offer.data?.company.name ?? t('sponsorships.offer.fallbackCompany');
  const takeover = offer.data?.takeover ?? null;
  const deferredUntil = offer.data?.deferredUntil ?? null;
  const busy = accept.isPending || defer.isPending;

  const handleNotNow = () => {
    if (!sponsorshipId) return;
    defer.mutate(
      { sponsorshipId },
      {
        onSuccess: () => onOpenChange(false),
        onError: () => toast.error(t('sponsorships.offer.card.actionFailed')),
      }
    );
  };

  const handleAccept = async () => {
    if (!sponsorshipId) return;
    try {
      await accept.mutateAsync({ sponsorshipId });
      toast.success(t('sponsorships.offer.accepted.toast'));
      onOpenChange(false);
      onAccepted?.();
    } catch (err) {
      // Our words, never the server's: an offer that is no longer open (the
      // company cancelled it, or it was already answered) says so.
      toast.error(
        apiErrorCode(err) === DOMAIN_ERROR_CODES.SPONSORSHIP_NOT_AWAITING_TALENT
          ? t('sponsorships.offer.errors.notOpen')
          : t('sponsorships.offer.errors.accept')
      );
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid="sponsorship-offer-dialog"
          closeLabel={t('common.close')}
        >
          <DialogHeader>
            <div className="flex items-center gap-3">
              <CompanyMark name={company} logo={offer.data?.company.logo ?? null} />
              <DialogTitle className="text-left">
                {t('sponsorships.offer.headline', {
                  company,
                })}
              </DialogTitle>
            </div>
            <DialogDescription className="text-left pt-2">
              {takeover
                ? t('sponsorships.offer.takeover.subheadline', { company })
                : t('sponsorships.offer.subheadline')}
            </DialogDescription>
          </DialogHeader>

          {offer.isLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" aria-hidden="true" />
              {t('sponsorships.offer.loading')}
            </div>
          ) : offer.isError ? (
            <div className="py-6 text-sm text-tone-rose">{t('sponsorships.offer.errors.load')}</div>
          ) : (
            <div className="space-y-4">
              {/* A journey the talent already paid for: the company takes it
                  over — what happens to what they paid, in the server's words. */}
              {takeover && (
                <div
                  className="rounded-xl border bg-tone-blue-subtle border-tone-blue-line p-4 space-y-1"
                  data-testid="sponsorship-offer-takeover"
                  data-kind={takeover.kind}
                >
                  <p className="text-sm font-semibold text-tone-blue inline-flex items-center gap-1.5">
                    <Wallet className="h-4 w-4" aria-hidden="true" />
                    {takeover.kind === 'pro'
                      ? t('sponsorships.offer.takeover.pro.title')
                      : t('sponsorships.offer.takeover.paid.title')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {takeover.kind === 'pro'
                      ? t('sponsorships.offer.takeover.pro.body', { company })
                      : takeover.refund
                        ? t('sponsorships.offer.takeover.paid.refund', {
                            company,
                            amount: formatCurrency(
                              takeover.refund.amountCents / 100,
                              takeover.refund.currency
                            ),
                          })
                        : t('sponsorships.offer.takeover.paid.noRefund', { company })}
                  </p>
                </div>
              )}

              {/* Unlocks list */}
              <div className="rounded-xl border bg-tone-violet-subtle border-tone-violet-line p-4">
                <p className="text-xs uppercase tracking-wider font-semibold text-tone-violet inline-flex items-center gap-1.5 mb-3">
                  <Sparkles className="h-3 w-3" aria-hidden="true" />
                  {t('sponsorships.offer.unlocks.title')}
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle2
                      className="h-4 w-4 text-tone-green shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <span>{t('sponsorships.offer.unlocks.checkup')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2
                      className="h-4 w-4 text-tone-green shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <span>{t('sponsorships.offer.unlocks.teleportal')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2
                      className="h-4 w-4 text-tone-green shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <span>{t('sponsorships.offer.unlocks.qultureup')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2
                      className="h-4 w-4 text-tone-green shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <span>{t('sponsorships.offer.unlocks.care')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2
                      className="h-4 w-4 text-tone-green shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <span>{t('sponsorships.offer.unlocks.passport')}</span>
                  </li>
                </ul>
              </div>

              {/* The journey the offer is for — named, so it is told apart from
                  the citizen's other journeys on the same route (R8). */}
              {offer.data && (
                <div className="space-y-1" data-testid="sponsorship-offer-journey">
                  <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                    <Plane className="h-3 w-3" aria-hidden="true" />
                    {t('sponsorships.offer.route', {
                      from: countryName(offer.data.journey.originCountry),
                      to: countryName(offer.data.journey.destinationCountry),
                    })}
                  </div>
                  {offer.data.sponsorship.dedicatedJourney ? (
                    // D-D — accepting creates a journey of the company's own;
                    // the one named here stays with the companies already on it.
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid="sponsorship-offer-dedicated"
                    >
                      {t('sponsorships.offer.dedicatedNote', { company })}
                    </p>
                  ) : (
                    <JourneyNameLine
                      label={offer.data.journey.label}
                      startedAt={offer.data.journey.startedAt}
                    />
                  )}
                </div>
              )}

              <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <Heart className="h-3 w-3 text-tone-rose" aria-hidden="true" />
                {takeover
                  ? t('sponsorships.offer.takeover.declineNote')
                  : t('sponsorships.offer.declineNote')}
              </p>
            </div>
          )}

          {deferredUntil && (
            <p className="text-xs text-muted-foreground" data-testid="sponsorship-offer-deferred">
              {t('sponsorships.offer.deferredUntil', { date: formatDate(deferredUntil) })}
            </p>
          )}

          {/* The invitation's answers: Accept first and full width, then "Not
              now", then Decline — terminal, so the quietest, and it asks. */}
          <DialogFooter className="flex-col gap-2 sm:flex-col pt-1">
            <Button
              onClick={handleAccept}
              disabled={busy || offer.isLoading || !sponsorshipId}
              className="w-full"
              data-testid="sponsorship-offer-accept"
            >
              {accept.isPending ? (
                <Loader2
                  className="h-4 w-4 mr-2 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" aria-hidden="true" />
              )}
              {t('sponsorships.offer.acceptCta')}
            </Button>
            <div className="flex items-center gap-2">
              {!deferredUntil && (
                <Button
                  variant="ghost"
                  onClick={handleNotNow}
                  disabled={busy || offer.isLoading || !sponsorshipId}
                  data-testid="sponsorship-offer-not-now"
                >
                  {t('sponsorships.offer.notNow')}
                </Button>
              )}
              <Button
                variant="link"
                className="ml-auto text-muted-foreground"
                onClick={() => setIsDeclineOpen(true)}
                disabled={busy || offer.isLoading}
                data-testid="sponsorship-offer-decline"
              >
                {t('sponsorships.offer.declineCta')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SponsorshipDeclineConfirm
        open={isDeclineOpen}
        onOpenChange={setIsDeclineOpen}
        sponsorshipId={sponsorshipId}
        company={company}
        keepsOwnAccess={takeover !== null}
        onDeclined={() => {
          setIsDeclineOpen(false);
          onOpenChange(false);
          onDeclined?.();
        }}
      />
    </>
  );
}
